import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import GenericFilterBar from "@/components/GenericFilterBar";
import SimpleChartGrid from "@/components/SimpleChartGrid";
import { NoAccess } from "@/components/NoAccess";
import { isForbiddenError } from "@/services/apiClient";
import { ChatDock } from "@/components/ChatDock";
import { MobileTopBar } from "@/components/MobileTopBar";
import UtilisationSkeleton from "@/components/UtilisationSkeleton";
import { getDefaultDateRange } from "@/analytics/api";
import { getChartColors } from "@/analytics/chart.utilities";
import {
  fetchDebtorsFilterOptions,
  fetchDebtorsDateRange,
  fetchDebtorsMonthly,
  fetchDebtorsMonthlyByAgeBand,
  fetchDebtorsRiskConcentration,
  fetchDebtorsByOfficeAgeBand,
  fetchDebtorsByServiceLine,
  fetchDebtorsByLocation,
  fetchDebtorsTopManagers,
  fetchDebtorsAvgAge,
  DEBTORS_DRILL_FILTER_KEYS,
  type DebtorFilters,
  type DebtorFilterOptions,
} from "@/analytics/debtors.api";
import { buildDrillThroughParams } from "@/lib/drillThroughTableQuery";
import {
  buildDebtorsChartItems,
  DEBTORS_CHART_KEYS,
  type DebtorsChartKey,
  type DebtorsChartData,
} from "@/analytics/charts/debtors.charts";
import { makeFilterCache } from "@/lib/filterPersistence";
import { displayAgeBandLabel } from "@/lib/ageBandLabels";
import { useChatVisibility } from "@/hooks/useChatVisibility";

const EMPTY_FILTERS: DebtorFilters = {
  service_lines: [], legacy_locations: [], managers: [], assignment_categories: [],
  age_band_names: [], date_from: null, date_to: null,
};

const MONTH_INDEX: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

const MAX_MONTH_CACHE_KEY = "prism:debtors:max_month";
const FILTERS_STORAGE_KEY = "prism:debtors:filters";
const readCachedMaxMonth = (): string | null => {
  try { return localStorage.getItem(MAX_MONTH_CACHE_KEY); } catch { return null; }
};
const writeCachedMaxMonth = (v: string | null) => {
  try {
    if (v) localStorage.setItem(MAX_MONTH_CACHE_KEY, v);
    else localStorage.removeItem(MAX_MONTH_CACHE_KEY);
  } catch { /* no-op */ }
};

const filterCache = makeFilterCache<DebtorFilters>("prism:debtors:filters");

const DebtorsPage = () => {
  const navigate = useNavigate();
  const savedFiltersRef = useRef<DebtorFilters | null>(readSavedDebtorFilters());
  const [opts, setOpts] = useState<DebtorFilterOptions>({
    service_lines: [], legacy_locations: [], managers: [], assignment_categories: [], age_band_names: [],
    min_month: null, max_month: null,
  });
  const [filters, setFilters] = useState<DebtorFilters>(() => {
    const persisted = filterCache.read();
    if (persisted) return persisted;
    const cached = readCachedMaxMonth();
    if (!cached) return EMPTY_FILTERS;
    const { date_from, date_to } = getDefaultDateRange(null, cached);
    return { ...EMPTY_FILTERS, date_from, date_to };
  });
  const [ready, setReady] = useState(() => readCachedMaxMonth() !== null);

  useEffect(() => { filterCache.write(filters); }, [filters]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(false);
  const [data, setData] = useState<DebtorsChartData>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [validatedFilterKey, setValidatedFilterKey] = useState<string | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useChatVisibility();
  const abortRef = useRef<AbortController | null>(null);
  const optionsAbortRef = useRef<AbortController | null>(null);
  const hasLoadedOnce = useRef(false);
  const palette = getChartColors().solid;

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("prism:sidebar:open");
      return saved !== null ? saved === "true" : true;
    } catch { return true; }
  });
  const handleSidebarChange = (open: boolean) => {
    setSidebarOpen(open);
    try { localStorage.setItem("prism:sidebar:open", String(open)); } catch { /* no-op */ }
  };

  useEffect(() => {
    Promise.all([fetchDebtorsFilterOptions(EMPTY_FILTERS), fetchDebtorsDateRange()])
      .then(([initialOptions, dr]) => {
        writeCachedMaxMonth(dr.max_month);
        const { date_from, date_to } = getDefaultDateRange(dr.min_month, dr.max_month);
        setOpts({ ...initialOptions, min_month: dr.min_month, max_month: dr.max_month });
        setFilters(prev => {
          if (!prev.date_from && !prev.date_to) return { ...prev, date_from, date_to };
          return prev.date_to === date_to ? prev : { ...prev, date_to };
        });
        setReady(true);
      })
      .catch(err => {
        // A total first-load failure used to only log, leaving every card
        // blank with no retry — which reads as "no data" rather than
        // "failed". Mark them all errored so each card offers Retry.
        console.error(err);
        setErrors(Object.fromEntries(DEBTORS_CHART_KEYS.map(k => [k, true])));
        setLoadingKeys(new Set());
        setReady(true);
      });
  }, []);

  useEffect(() => {
    const defaults = getDefaultDateRange(opts.min_month, opts.max_month);
    const isDefault = !filters.service_lines.length && !filters.legacy_locations.length && !filters.managers.length && !filters.assignment_categories.length && !filters.age_band_names.length && filters.date_from === defaults.date_from && filters.date_to === defaults.date_to;
    try { isDefault ? localStorage.removeItem(FILTERS_STORAGE_KEY) : localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters)); } catch { /* no-op */ }
  }, [filters, opts.min_month, opts.max_month]);

  useEffect(() => {
    if (!ready) return;
    optionsAbortRef.current?.abort();
    const controller = new AbortController();
    optionsAbortRef.current = controller;
    const requestedKey = getDebtorFilterKey(filters);
    setOptionsLoading(true);
    setValidatedFilterKey(null);

    const timeout = window.setTimeout(() => {
      fetchDebtorsFilterOptions(filters, controller.signal)
        .then(nextOptions => {
          if (controller.signal.aborted) return;
          setOpts(prev => ({ ...nextOptions, min_month: prev.min_month, max_month: prev.max_month }));
          setValidatedFilterKey(requestedKey);
        })
        .catch(err => {
          if (!controller.signal.aborted && err?.name !== "AbortError") {
            console.error(err);
            setValidatedFilterKey(requestedKey);
          }
        })
        .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    }, 250);

    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [filters, ready]);

  const fetcherMap = (f: DebtorFilters, signal?: AbortSignal) => ({
    monthly:  () => fetchDebtorsMonthly(f, signal).then(d => ({ monthly: d.data })),
    byAge:    () => fetchDebtorsMonthlyByAgeBand(f, signal).then(d => ({ byAge: d.data })),
    risk:     () => fetchDebtorsRiskConcentration(f, signal).then(d => ({ risk: d.data })),
    byOffice: () => fetchDebtorsByOfficeAgeBand(f, signal).then(d => ({ byOffice: d.data })),
    bySL:     () => fetchDebtorsByServiceLine(f, signal).then(d => ({ bySL: d })),
    byLoc:    () => fetchDebtorsByLocation(f, signal).then(d => ({ byLoc: d })),
    topMgrs:  () => fetchDebtorsTopManagers(f, 10, signal).then(d => ({ topMgrs: d.data })),
    avgAge:   () => fetchDebtorsAvgAge(f, signal).then(d => ({ avgAge: d.data })),
  });

  useEffect(() => {
    if (!ready || validatedFilterKey !== getDebtorFilterKey(filters)) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const { signal } = ctrl;
    setNoAccess(false);

    const isFirst = !hasLoadedOnce.current;
    const fetchers = fetcherMap(filters, signal);

    if (isFirst) {
      setInitialLoading(true);
      Promise.all(DEBTORS_CHART_KEYS.map(k => fetchers[k]()))
        .then(results => {
          if (signal.aborted) return;
          const merged = results.reduce((acc, r) => ({ ...acc, ...r }), {} as DebtorsChartData);
          setData(merged);
          hasLoadedOnce.current = true;
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) setNoAccess(true);
          else console.error(err);
        })
        .finally(() => {
          if (!signal.aborted) setInitialLoading(false);
        });
      return;
    }

    setLoadingKeys(new Set(DEBTORS_CHART_KEYS));
    setErrors({});

    DEBTORS_CHART_KEYS.forEach(k => {
      fetchers[k]()
        .then(r => {
          if (signal.aborted) return;
          setData(s => ({ ...s, ...r }));
          setLoadingKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) { setNoAccess(true); return; }
          setErrors(prev => ({ ...prev, [k]: true }));
          setLoadingKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
        });
    });
  }, [filters, ready, validatedFilterKey]);

  const retryChart = useCallback((key: DebtorsChartKey) => {
    setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    setLoadingKeys(prev => new Set([...prev, key]));
    const fetchers = fetcherMap(filters);
    fetchers[key]()
      .then(r => setData(s => ({ ...s, ...r })))
      .catch(() => setErrors(prev => ({ ...prev, [key]: true })))
      .finally(() => {
        setLoadingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
      });
  }, [filters]);

  const toggle = (key: string, value: string) => {
    setFilters(prev => {
      const cur = prev[key as keyof DebtorFilters] as string[];
      return { ...prev, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  const clearAll = () => {
    const { date_from, date_to } = getDefaultDateRange(opts.min_month, opts.max_month);
    setFilters({ ...EMPTY_FILTERS, date_from, date_to });
  };

  const defaultRange = getDefaultDateRange(opts.min_month, opts.max_month);
  const hasActive =
    filters.service_lines.length || filters.legacy_locations.length || filters.managers.length ||
    filters.assignment_categories.length || filters.age_band_names.length ||
    filters.date_from !== defaultRange.date_from || filters.date_to !== defaultRange.date_to;

  function handleDebtorsDrillThroughButtonClick() {
    const month = getDebtorsDrillThroughMonth();
    if (!month) return;
    navigate(buildDebtorsDrillThroughUrl(month, filters));
  }

  function getDebtorsDrillThroughMonth() {
    return getLatestVisibleMonth(data.monthly);
  }

  const items = useMemo(
    () => buildDebtorsChartItems({
      data,
      errors,
      loadingKeys,
      retryChart,
      palette,
    }),
    [data, errors, loadingKeys, palette, retryChart],
  );

  const anyLoading = loadingKeys.size > 0;

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange}>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title="Debtors (Aged)" />
          <main className="flex-1 flex overflow-hidden min-h-0">
            <div className="flex-1 min-w-0 p-2 sm:p-3 space-y-4 sm:space-y-6 overflow-auto">
              {noAccess ? (
                <NoAccess />
              ) : initialLoading ? (
                <UtilisationSkeleton count={items.length} filterCount={5} />
              ) : (
                <>
                  <GenericFilterBar
                    fields={[
                      { key: "service_lines", label: "Service Line", options: [...new Set([...opts.service_lines, ...filters.service_lines])], availableOptions: opts.service_lines, selected: filters.service_lines },
                      { key: "legacy_locations", label: "Location", options: [...new Set([...opts.legacy_locations, ...filters.legacy_locations])], availableOptions: opts.legacy_locations, selected: filters.legacy_locations },
                      { key: "managers", label: "Manager", options: [...new Set([...opts.managers, ...filters.managers])], availableOptions: opts.managers, selected: filters.managers, searchable: true },
                      { key: "assignment_categories", label: "Category", options: [...new Set([...opts.assignment_categories, ...filters.assignment_categories])], availableOptions: opts.assignment_categories, selected: filters.assignment_categories },
                      { key: "age_band_names", label: "Age Band", options: [...new Set([...opts.age_band_names, ...filters.age_band_names])], availableOptions: opts.age_band_names, selected: filters.age_band_names, labelFor: displayAgeBandLabel },
                    ]}
                    onToggle={toggle}
                    onClearAll={clearAll}
                    min_month={opts.min_month}
                    max_month={opts.max_month}
                    date_from={filters.date_from}
                    date_to={filters.date_to}
                    onDateChange={(date_from, date_to) => setFilters(prev => ({ ...prev, date_from, date_to }))}
                    isLoading={anyLoading || optionsLoading}
                    hasActiveFilters={!!hasActive}
                  />
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      onClick={handleDebtorsDrillThroughButtonClick}
                      disabled={!getDebtorsDrillThroughMonth()}
                    >
                      <Search className="h-4 w-4" />
                      Drill Through
                    </Button>
                  </div>
                  <SimpleChartGrid items={items} />
                </>
              )}
            </div>
            <ChatDock
              open={isAssistantOpen}
              onOpenChange={setIsAssistantOpen}
              chartItems={items}
            />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

function getMonthStart(monthLabel: string | null | undefined): string | null {
  if (!monthLabel) return null;
  const parsed = parseMonthLabel(monthLabel);
  if (!parsed) return null;

  const first = new Date(Date.UTC(parsed.year, parsed.month, 1));
  return first.toISOString().slice(0, 10);
}

function parseMonthLabel(monthLabel: string): { year: number; month: number } | null {
  const trimmed = monthLabel.trim();
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
  if (isoMatch) return { year: Number(isoMatch[1]), month: Number(isoMatch[2]) - 1 };

  const match = trimmed.match(/^([A-Za-z]+)\s+(\d{4})$/) ?? trimmed.match(/^(\d{4})\s+([A-Za-z]+)$/);
  if (!match) return null;

  const monthPart = Number(match[1]) ? match[2] : match[1];
  const yearPart = Number(match[1]) ? match[1] : match[2];
  const month = MONTH_INDEX[monthPart.toLowerCase()];
  const year = Number(yearPart);
  if (month === undefined || !Number.isFinite(year)) return null;
  return { year, month };
}

function getLatestVisibleMonth(rows: unknown): string | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const latest = rows[rows.length - 1] as { monthLabel?: string };
  return getMonthStart(latest.monthLabel);
}

function buildDebtorsDrillThroughUrl(month: string, filters: DebtorFilters): string {
  const params = buildDrillThroughParams({
    month,
    filters: filters as unknown as Record<string, string[] | string | null | undefined>,
    filterKeys: [...DEBTORS_DRILL_FILTER_KEYS],
  });
  return `/debtors-current/drill-through?${params.toString()}`;
}

export default DebtorsPage;

function getDebtorFilterKey(filters: DebtorFilters): string {
  return JSON.stringify(filters);
}

function readSavedDebtorFilters(): DebtorFilters | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) ?? "null");
    if (!parsed || typeof parsed !== "object") return null;
    const asStrings = (value: unknown) => Array.isArray(value) && value.every(item => typeof item === "string") ? value : [];
    return {
      service_lines: asStrings(parsed.service_lines),
      legacy_locations: asStrings(parsed.legacy_locations),
      managers: asStrings(parsed.managers),
      assignment_categories: asStrings(parsed.assignment_categories),
      age_band_names: asStrings(parsed.age_band_names),
      date_from: typeof parsed.date_from === "string" ? parsed.date_from : null,
      date_to: typeof parsed.date_to === "string" ? parsed.date_to : null,
    };
  } catch { return null; }
}
